import { ensureFreshSession } from "@auth/refreshMiddleware";
import {
  createMicSource,
  MicrophoneError,
  type PcmSource,
} from "@transcription/pcmSource";
import {
  beginRecording,
  enqueueChunk,
  finishRecording,
  setRecordingTranscript,
} from "@transcription/recordingsClient";
import { useCallback, useEffect, useRef, useState } from "react";

import { getConfig } from "../config";

type TranscriptionStatus =
  | "idle"
  | "connecting"
  | "recording"
  | "reconnecting"
  | "stopping"
  | "error";

interface Turn {
  order: number;
  sourceOrder?: number;
  transcriptUuid?: string;
  revision?: string;
  transcript: string;
}

export interface StartOptions {
  source?: PcmSource;
  roomUuid?: string;
  // A provider so each (re)connect gets a fresh token; defaults to the user session.
  wsToken?: () => Promise<string>;
}

export interface UseLiveTranscriptionResult {
  status: TranscriptionStatus;
  /** First socket-open time for this recording; reconnects keep the same clock. */
  startedAt: number | null;
  turns: Turn[];
  partial: string;
  error: string | null;
  /** `error` came from local capture (`MicrophoneError`), not the socket. */
  micFailure: boolean;
  start: (options?: StartOptions) => Promise<void>;
  stop: () => void;
  noAudioWarning: boolean;
  /** Latched on the first transcriber turn until the next `start()`: "we have heard you", not "we hear you now". */
  audioDetected: boolean;
}

const DEFAULT_WS_URL = getConfig().wsUrl;
const MAX_TURNS = 500;
const STOP_TIMEOUT_MS = 3000;
const FLUSH_BYTES = 512 * 1024;
const MAX_RECONNECTS = 12;
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 15_000;
const NO_AUDIO_TIMEOUT_MS = 15_000;

function concatBuffers(chunks: ArrayBuffer[]): ArrayBuffer {
  const total = chunks.reduce((n, c) => n + c.byteLength, 0);
  const merged = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(new Uint8Array(chunk), offset);
    offset += chunk.byteLength;
  }
  return merged.buffer;
}

export function useLiveTranscription(): UseLiveTranscriptionResult {
  const wsUrl = `${DEFAULT_WS_URL}/ws/transcription/stream`;
  const [status, setStatus] = useState<TranscriptionStatus>("idle");
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [partial, setPartial] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [micFailure, setMicFailure] = useState(false);
  const [noAudioWarning, setNoAudioWarning] = useState(false);
  const [audioDetected, setAudioDetected] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const sourceRef = useRef<PcmSource | null>(null);
  const readyResolveRef = useRef<(() => void) | null>(null);
  const turnOrderRef = useRef(0);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const noAudioTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const audioDetectedRef = useRef(false);
  const noAudioArmedRef = useRef(false);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const openSocketRef = useRef<(() => Promise<void>) | null>(null);
  const scheduleReconnectRef = useRef<(() => void) | null>(null);
  const userStoppingRef = useRef(false);
  const recordingIdRef = useRef<string | null>(null);
  const batchRef = useRef<ArrayBuffer[]>([]);
  const batchBytesRef = useRef(0);
  const seqRef = useRef(0);
  const stopRef = useRef<(() => void) | null>(null);
  const wsTokenRef = useRef<(() => Promise<string>) | null>(null);

  const finalizeRecording = useCallback(() => {
    const recordingId = recordingIdRef.current;
    recordingIdRef.current = null;
    if (!recordingId) return;
    const tail = batchRef.current;
    batchRef.current = [];
    batchBytesRef.current = 0;
    finishRecording(recordingId, tail, seqRef.current).catch(() => {});
  }, []);

  const teardownSocket = useCallback(() => {
    const ws = wsRef.current;
    wsRef.current = null;
    if (!ws) return;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    if (
      ws.readyState !== WebSocket.CLOSED &&
      ws.readyState !== WebSocket.CLOSING
    )
      ws.close();
  }, []);

  const cleanup = useCallback(() => {
    setStartedAt(null);
    finalizeRecording();
    if (stopTimerRef.current !== null) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (noAudioTimerRef.current !== null) {
      clearTimeout(noAudioTimerRef.current);
      noAudioTimerRef.current = null;
    }
    setNoAudioWarning(false);
    setAudioDetected(false);
    sourceRef.current?.stop();
    sourceRef.current = null;
    // Release any source still awaiting socket-open so it unwinds.
    readyResolveRef.current?.();
    readyResolveRef.current = null;
    wsTokenRef.current = null;
    teardownSocket();
  }, [finalizeRecording, teardownSocket]);

  // Two sinks: the live socket (best-effort) and the resumable IndexedDB recording that becomes the artifact.
  const handleChunk = useCallback((chunk: ArrayBuffer) => {
    if (!noAudioArmedRef.current && !userStoppingRef.current) {
      noAudioArmedRef.current = true;
      noAudioTimerRef.current = setTimeout(() => {
        noAudioTimerRef.current = null;
        if (!audioDetectedRef.current) setNoAudioWarning(true);
      }, NO_AUDIO_TIMEOUT_MS);
    }
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(chunk);
    const recordingId = recordingIdRef.current;
    if (!recordingId) return;
    batchRef.current.push(chunk);
    batchBytesRef.current += chunk.byteLength;
    if (batchBytesRef.current >= FLUSH_BYTES) {
      const merged = concatBuffers(batchRef.current);
      batchRef.current = [];
      batchBytesRef.current = 0;
      enqueueChunk(recordingId, seqRef.current++, merged);
    }
  }, []);

  // Rebuilds only the WebSocket; capture and local persistence keep running.
  const scheduleReconnect = useCallback(() => {
    if (userStoppingRef.current) return;
    teardownSocket();
    if (reconnectAttemptsRef.current >= MAX_RECONNECTS) {
      setError("Connection lost");
      setStatus("error");
      cleanup();
      return;
    }
    reconnectAttemptsRef.current += 1;
    setStatus("reconnecting");
    const wait = Math.min(
      RECONNECT_MAX_MS,
      RECONNECT_BASE_MS * 2 ** (reconnectAttemptsRef.current - 1),
    );
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      openSocketRef.current?.().catch(() => scheduleReconnectRef.current?.());
    }, wait);
  }, [cleanup, teardownSocket]);

  useEffect(() => {
    scheduleReconnectRef.current = scheduleReconnect;
  }, [scheduleReconnect]);

  const handleMessage = useCallback(
    (raw: string) => {
      let msg: {
        type?: string;
        transcript?: string;
        transcript_uuid?: string;
        end_of_turn?: boolean;
        turn_order?: number;
        revision?: string;
      };
      try {
        msg = JSON.parse(raw);
      } catch {
        return;
      }
      // Counterpart: transcription/stream.py; revisions are scoped to the provider session.
      if (
        msg.type === "TranscriptRevision" &&
        typeof msg.transcript === "string"
      ) {
        const text = msg.transcript;
        if (
          typeof msg.turn_order !== "number" ||
          !msg.transcript_uuid ||
          typeof msg.revision !== "string"
        )
          return;
        const revision = msg.revision;
        setTurns((previous) =>
          previous.map((turn) =>
            turn.sourceOrder === msg.turn_order &&
            turn.transcriptUuid === msg.transcript_uuid &&
            (!turn.revision || revision > turn.revision)
              ? { ...turn, transcript: text, revision }
              : turn,
          ),
        );
        return;
      }
      if (msg.type === "Turn" && typeof msg.transcript === "string") {
        // The ref is what the no-audio timer callback reads; the state mirrors it for rendering.
        if (!audioDetectedRef.current) {
          audioDetectedRef.current = true;
          setAudioDetected(true);
          if (noAudioTimerRef.current !== null) {
            clearTimeout(noAudioTimerRef.current);
            noAudioTimerRef.current = null;
          }
          setNoAudioWarning(false);
        }
        const text = msg.transcript;
        if (msg.end_of_turn) {
          const order = turnOrderRef.current++;
          setTurns((prev) => {
            const next = [
              ...prev,
              {
                order,
                sourceOrder: msg.turn_order,
                transcriptUuid: msg.transcript_uuid,
                transcript: text,
              },
            ];
            return next.length > MAX_TURNS
              ? next.slice(next.length - MAX_TURNS)
              : next;
          });
          setPartial("");
        } else {
          setPartial(text);
        }
      } else if (msg.type === "Termination") {
        if (userStoppingRef.current) {
          setStatus("idle");
          cleanup();
        } else {
          scheduleReconnect();
        }
      } else if (msg.type === "Begin") {
        reconnectAttemptsRef.current = 0;
        setStatus("recording");
      } else if (msg.type === "started") {
        const recordingId = recordingIdRef.current;
        if (recordingId && msg.transcript_uuid)
          setRecordingTranscript(recordingId, msg.transcript_uuid).catch(
            () => {},
          );
      }
    },
    [cleanup, scheduleReconnect],
  );

  const openSocket = useCallback(async () => {
    const token = wsTokenRef.current
      ? await wsTokenRef.current()
      : (await ensureFreshSession())?.accessToken;
    if (userStoppingRef.current) return;
    if (!token) throw new Error("Not authenticated");
    const ws = new WebSocket(wsUrl, ["scribenote.auth.bearer.v1", token]);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: "start", keyterms: [] }));
      const openedAt = Date.now();
      setStartedAt((previous) => previous ?? openedAt);
      setStatus((s) => (s === "stopping" ? s : "recording"));
      readyResolveRef.current?.();
      readyResolveRef.current = null;
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data === "string") handleMessage(ev.data);
    };
    ws.onerror = scheduleReconnect;
    ws.onclose = () => {
      if (!userStoppingRef.current) scheduleReconnect();
    };
  }, [wsUrl, handleMessage, scheduleReconnect]);

  useEffect(() => {
    openSocketRef.current = openSocket;
  }, [openSocket]);

  const start = useCallback(
    async (options?: StartOptions) => {
      if (wsRef.current || sourceRef.current) return;
      if (status !== "idle" && status !== "error") return;
      setError(null);
      setMicFailure(false);
      setTurns([]);
      setPartial("");
      turnOrderRef.current = 0;
      userStoppingRef.current = false;
      wsTokenRef.current = options?.wsToken ?? null;
      reconnectAttemptsRef.current = 0;
      audioDetectedRef.current = false;
      noAudioArmedRef.current = false;
      setNoAudioWarning(false);
      setAudioDetected(false);
      const recordingId = crypto.randomUUID();
      recordingIdRef.current = recordingId;
      beginRecording(recordingId, options?.roomUuid).catch(() => {});
      batchRef.current = [];
      batchBytesRef.current = 0;
      seqRef.current = 0;
      setStartedAt(null);
      setStatus("connecting");

      const source = options?.source ?? createMicSource();
      sourceRef.current = source;
      const whenReady = new Promise<void>((resolve) => {
        readyResolveRef.current = resolve;
      });

      try {
        await source.start({
          emit: handleChunk,
          whenReady,
          onEnded: () => stopRef.current?.(),
        });
        await openSocket();
      } catch (e) {
        setMicFailure(e instanceof MicrophoneError);
        setError(e instanceof Error ? e.message : String(e));
        setStatus("error");
        cleanup();
      }
    },
    [status, openSocket, handleChunk, cleanup],
  );

  const stop = useCallback(() => {
    if (
      status !== "recording" &&
      status !== "connecting" &&
      status !== "reconnecting"
    )
      return;
    userStoppingRef.current = true;
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    setStatus("stopping");
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: "stop" }));
      } catch {
        cleanup();
        setStatus("idle");
        return;
      }
      stopTimerRef.current = setTimeout(() => {
        stopTimerRef.current = null;
        cleanup();
        setStatus("idle");
      }, STOP_TIMEOUT_MS);
    } else {
      cleanup();
      setStatus("idle");
    }
  }, [status, cleanup]);

  useEffect(() => {
    stopRef.current = stop;
  }, [stop]);

  useEffect(() => cleanup, [cleanup]);

  return {
    status,
    startedAt,
    turns,
    partial,
    error,
    micFailure,
    start,
    stop,
    noAudioWarning,
    audioDetected,
  };
}
